import { debounce } from 'lodash';
import React, { memo, useState, useCallback } from 'react';

import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableRow from '@mui/material/TableRow';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Autocomplete from '@mui/material/Autocomplete';
import TableContainer from '@mui/material/TableContainer';

import useAuth from '../../../hooks/useAuth';
import useLocales from '../../../hooks/useLocales';
import { isValidEmail } from '../utils/validations';
import useAppSettings from '../../../hooks/useAppSettings';
import { UserItem } from '../../../@types/users/user-item';
import { UsersFilter } from '../../../@types/users/usersFilter';
import type { Collaborator } from '../../../@types/forms/types';
import aspUserService from '../../../services/asp-identity/aspUserService';
import type { FieldErrors, CollaboratorFieldsGroup } from '../utils/formValidationUtils';

// ----------------------------------------------------------------------

interface CollaboratorsWithFieldsTableProps {
  collaborators: Collaborator[];
  setCollaborators: React.Dispatch<React.SetStateAction<Collaborator[]>>;
  collaboratorsErrors: string[];
  setCollaboratorsErrors: React.Dispatch<React.SetStateAction<string[]>>;
  fieldsGroupedByCollaborator: CollaboratorFieldsGroup[];
  fieldErrors: FieldErrors;
  onFieldClick: (fieldId: string) => void;
  isEditMode?: boolean;
}

const CollaboratorsWithFieldsTable: React.FC<CollaboratorsWithFieldsTableProps> = ({
  collaborators,
  setCollaborators,
  collaboratorsErrors,
  setCollaboratorsErrors,
  fieldsGroupedByCollaborator,
  fieldErrors,
  onFieldClick,
  isEditMode = false,
}) => {
  const { t } = useLocales();
  const { user } = useAuth();
  const moduleData = useAppSettings();
  const [userItems, setUserItems] = useState<UserItem[]>([]);

  const fetchUserItems = async (query: string) => {
    if (moduleData.isCompany) {
      const filter = new UsersFilter();
      filter.searchQuery = query;
      filter.offset = 0;
      filter.limit = 10;

      const response = await aspUserService.getUserItems(filter);
      const { total, items } = response;

      if (total > 0) {
        const newUserItems = items.filter((u: UserItem) => !u.isDeactivated);
        if (newUserItems.length > 0) {
          setUserItems(newUserItems);
        }
      }
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceFetchUserItems = useCallback(debounce(fetchUserItems, 500), []);

  const handleSearchQueryChange = (query: string) => {
    if (!query) {
      setUserItems([]);
      return;
    }
    debounceFetchUserItems(query);
  };

  const handleCollaboratorEmailChange = useCallback(
    (collaboratorId: string, email: string) => {
      setCollaborators((prevCollaborators) =>
        prevCollaborators.map((c) => (c.collaboratorId === collaboratorId ? { ...c, email } : c))
      );

      setCollaboratorsErrors((prevErrors) => {
        if (prevErrors.includes(collaboratorId) && isValidEmail(email)) {
          return prevErrors.filter((e) => e !== collaboratorId);
        }
        return prevErrors;
      });
    },
    [setCollaborators, setCollaboratorsErrors]
  );

  if (!collaborators || collaborators.length === 0) {
    return <Alert severity="error">Error: No form fillers.</Alert>;
  }

  const totalFields = fieldsGroupedByCollaborator.reduce(
    (acc, group) => acc + group.fields.length,
    0
  );

  const requiredFields = fieldsGroupedByCollaborator.reduce(
    (acc, group) => acc + group.fields.filter((field) => field.required).length,
    0
  );

  const isOwner = collaborators.find(
    (c) => c.role === 'owner' && c.email?.toLowerCase() === user?.email?.toLowerCase()
  );

  // Sort collaborators by order
  const sortedCollaborators = [...collaborators].sort((a, b) => a.order - b.order);

  return (
    <TableContainer>
      <Table size="small" aria-label="collaborators with fields table">
        {totalFields > 0 && (
          <TableHead>
            <TableRow>
              <TableCell align="left">Title</TableCell>
              <TableCell align="center" sx={{ width: '1%', whiteSpace: 'nowrap' }}>
                Page
              </TableCell>
            </TableRow>
          </TableHead>
        )}

        <TableBody>
          {sortedCollaborators.map((collaborator, index) => {
            const group = fieldsGroupedByCollaborator.find(
              (g) => g.collaboratorId === collaborator.collaboratorId
            );
            const hasError = collaboratorsErrors.includes(collaborator.collaboratorId);
            const isCurrentUser = collaborator.email?.toLowerCase() === user?.email?.toLowerCase();

            let helperText = '';
            if (hasError && (!collaborator.email || collaborator.email.length === 0)) {
              helperText = t('forms.validationErrorMessage.fieldRequired');
            }
            if (hasError && collaborator.email && collaborator.email.length > 0) {
              helperText = t('forms.validationErrorMessage.invalidEmailFormat');
            }

            return (
              <React.Fragment key={collaborator.collaboratorId}>
                {/* Spacer row between collaborators */}
                {index > 0 && (
                  <TableRow>
                    <TableCell colSpan={2} sx={{ py: 1, border: 'none' }} />
                  </TableRow>
                )}
                {/* Collaborator header with email input */}
                <TableRow>
                  <TableCell
                    colSpan={2}
                    sx={{
                      backgroundColor: collaborator.color,
                      py: 1.5,
                      opacity: isEditMode && !isCurrentUser ? 0.5 : 1,
                      borderTopLeftRadius: 8,
                      borderTopRightRadius: 8,
                    }}
                  >
                    {isEditMode ? (
                      // Edit mode - show email as text
                      <Stack>
                        <Typography
                          variant="body1"
                          color="inherit"
                          sx={{
                            fontWeight: isCurrentUser ? 700 : 400,
                          }}
                        >
                          {collaborator.email}
                        </Typography>
                        <Typography variant="caption" color="inherit">
                          {collaborator.role === 'owner'
                            ? t('forms.owner')
                            : t('forms.collaborator', { number: collaborator.order })}
                        </Typography>
                      </Stack>
                    ) : (
                      // Create mode - show email input for collaborators, text for owner
                      <>
                        {collaborator.role === 'owner' ? (
                          <Stack>
                            <Typography
                              variant="body1"
                              color="inherit"
                              sx={{
                                fontWeight: 700,
                              }}
                            >
                              {collaborator.email || user?.email}
                            </Typography>
                            <Typography variant="caption" color="inherit">
                              {t('forms.owner')}
                            </Typography>
                          </Stack>
                        ) : (
                          <Autocomplete
                            value={collaborator.email || ''}
                            disabled={!isOwner}
                            fullWidth
                            freeSolo
                            size="small"
                            disableClearable
                            options={userItems}
                            getOptionLabel={(option: UserItem | string) =>
                              typeof option === 'string' ? option : option.email
                            }
                            onFocus={() => {
                              handleSearchQueryChange(collaborator.email || '');
                            }}
                            onChange={(_, value) => {
                              const email = typeof value === 'string' ? value : value?.email || '';
                              handleCollaboratorEmailChange(collaborator.collaboratorId, email);
                            }}
                            onInputChange={(_, value) => {
                              handleSearchQueryChange(value);
                              handleCollaboratorEmailChange(collaborator.collaboratorId, value);
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                id={collaborator.collaboratorId}
                                label={t('forms.collaborator', { number: collaborator.order })}
                                variant="outlined"
                                placeholder={t('shared.fields.email')}
                                error={hasError}
                                helperText={helperText}
                                onFocus={() => setUserItems([])}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    backgroundColor: 'background.paper',
                                  },
                                }}
                              />
                            )}
                            renderOption={(props, option) => (
                              <Box component="li" {...props}>
                                {`${(option as UserItem).firstName} ${(option as UserItem).lastName} (${(option as UserItem).email})`}
                              </Box>
                            )}
                          />
                        )}
                      </>
                    )}
                  </TableCell>
                </TableRow>
                {/* Fields for this collaborator */}
                {group?.fields.map((field) => (
                  <TableRow
                    key={field.id}
                    onClick={() => onFieldClick(field.id)}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'action.hover' },
                      borderLeft: 4,
                      borderLeftColor: collaborator.color,
                      opacity: isEditMode && !isCurrentUser ? 0.5 : 1,
                    }}
                  >
                    <TableCell
                      align="left"
                      sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
                    >
                      <Stack>
                        <Stack
                          direction="row"
                          spacing={0.5}
                          sx={{
                            alignItems: 'center',
                          }}
                        >
                          <Link
                            href="#"
                            variant="subtitle1"
                            color={fieldErrors[field.id] ? 'error' : 'secondary'}
                            onClick={(e) => e.preventDefault()}
                          >
                            {field.label}
                          </Link>
                          {field.required && (
                            <Typography
                              component="span"
                              color="error"
                              sx={{
                                fontWeight: 'bold',
                              }}
                            >
                              *
                            </Typography>
                          )}
                        </Stack>

                        {fieldErrors[field.id] && (
                          <Typography variant="caption" color="error">
                            {fieldErrors[field.id].message}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>

                    <TableCell
                      align="center"
                      sx={{
                        width: '1%',
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Typography color="secondary">{field.page}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
      {totalFields > 0 && (
        <Typography variant="caption" color="textDisabled" sx={{ ml: 2, display: 'block' }}>
          {`${t('documents.totalFields')}: ${totalFields}, ${t('documents.totalRequiredFields')}: ${requiredFields}.`}
        </Typography>
      )}
    </TableContainer>
  );
};

export default memo(CollaboratorsWithFieldsTable);
